import axios from 'axios';

import { digestMessage, generateRandomId, getBaseUrl } from '@/components/tracking_third_party/utils/fbcap.utils';
import { TmCart } from '@/tscapi/model/cart';
import { isConsentCategoryDisabled } from '@/tscapi/utils/consent';
import { Cookie } from '@/tscapi/utils/cookie';
declare global {
    interface Window {
        ttq: any;
        fbq: any;
        _fbq: any;
    }
}

type FbPixelObject = {
    content_ids: string[];
    content_type: string;
    currency: string;
    content_name?: string;
    value: number;
    contents: { id: string; quantity: number; item_price: number; delivery_category: string }[];
    test_event_code?: string;
    user_data?: {
        em?: string;
        ph?: string;
        fn?: string;
        ln?: string;
        client_ip_address?: string;
        client_user_agent?: string;
    };
};

type CapiObject = {
    test_event_code?: string;
    data: {
        event_name: string;
        event_time: number;
        event_id: string;
        action_source: string;
        event_source_url: string;
        user_data?: {
            em?: string;
            ph?: string;
            fn?: string;
            ln?: string;
            client_ip_address?: string;
            client_user_agent?: string;
            ct?: string[]; //city
        };
        custom_data?: {
            content_ids: string[];
            content_type: string;
            content_name?: string;
            currency: string;
            value: number;
            contents: { id: string; quantity: number; item_price: number; delivery_category: string }[];
        };
    }[];
};
type EventData = {
    cart: TmCart;
    edpNo: number;
    eventId: string;
    quantity: number;
};

document.addEventListener(
    'addBagEvent',
    function (e: CustomEvent) {
        // console.log('Event: FB add-to-cart');
        fbAddToCartEvent(e?.detail);
    },
    false,
);

async function fbAddToCartEvent(eventDetails: EventData) {
    //if consent is not given, prevent fb event
    if (isConsentCategoryDisabled('targeting')) {
        return;
    }

    // if there is no edpNo then this event triggered
    // from removing an item in the slideout cart so we return early
    if (!eventDetails.edpNo) return;

    const currentHostname = window.location.hostname;
    const url = getBaseUrl(currentHostname);
    // console.log('Event: FB add-to-cart', eventDetails);
    const randomEventNumber = generateRandomId(15);
    const fbObject = await generateFacebookObject(eventDetails, randomEventNumber);
    const fbPixelObject = await generateFacebookPixelObject(eventDetails);

    const axiosInstance = axios.create({
        baseURL: url,
        withCredentials: true,
        headers: {
            Authorization: 'Basic 567e407f-4bc5-463e-9c28-3ac2a2b333ec',
            'Content-Type': 'application/json',
        },
    });

    // server event
    if (window.tscConfig?.CapiEnabled == 'true') {
        try {
            await axiosInstance.post('/tracking/v1/events/', fbObject);
        } catch (error) {
            console.log('Error sending FB conversion event:', error?.response?.data || error.message);
        }
    }

    // pixel event
    try {
        window.fbq('track', 'AddToCart', fbPixelObject, { eventID: randomEventNumber, event_name: 'AddToCart' });
    } catch (e) {
        console.log('Error sending FB pixel conversion event');
    }
}

async function generateFacebookObject(eventData: EventData, randomEventNumber) {
    const cookie = new Cookie();
    const userAgent = window.navigator.userAgent || '';
    const cartLine = eventData.cart.CartLines.find((cartLine) => cartLine.CartLineItem.EdpNo === eventData.edpNo);
    const fbCapiObject: CapiObject = {
        data: [
            {
                event_name: 'AddToCart',
                event_id: randomEventNumber,
                event_time: Math.floor(Date.now() / 1000),
                action_source: 'website',
                event_source_url: window.location.href,
                user_data: {},
                custom_data: {
                    content_ids: cartLine ? [cartLine.CartLineItem.ItemNo] : [],
                    content_type: 'product_group',
                    currency: 'CAD',
                    content_name: eventData.cart.Products.find(
                        (product) => product.ItemNo === cartLine?.CartLineItem.ItemNo,
                    ).Name,
                    value: cartLine.InCart.PriceUnit, // eventData.cart.OrderSummary.GrandTotalAmount.toString(), //.MerchandiseSubTotal ?
                    contents: cartLine
                        ? [
                              {
                                  id: cartLine.CartLineItem.ItemNo,
                                  item_price: cartLine.InCart.PriceUnit,
                                  delivery_category: 'home_delivery',
                                  quantity: eventData.quantity,
                              },
                          ]
                        : [],
                },
            },
        ],
    };

    //add test code if exits
    if (window.tscConfig?.CapiTestCode) {
        fbCapiObject.test_event_code = window.tscConfig.CapiTestCode;
    }
    //add client ip and useragent
    if (cookie.TscClientIP) {
        fbCapiObject.data[0].user_data.client_ip_address = cookie.TscClientIP;
    }
    if (userAgent) {
        fbCapiObject.data[0].user_data.client_user_agent = userAgent;
    }

    // If user is signed in, add user data
    if (cookie.UserAccessToken?.Token?.length > 0 && cookie.UserAccessToken.Edp > 0) {
        if (cookie.UserEmail) {
            fbCapiObject.data[0].user_data.em = await digestMessage(cookie.UserEmail.toLowerCase());
        }
        if (cookie.UserName) {
            fbCapiObject.data[0].user_data.fn = await digestMessage(cookie.UserName.toLowerCase());
        }
        if (cookie.LastName) {
            fbCapiObject.data[0].user_data.ln = await digestMessage(cookie.LastName.toLowerCase());
        }
    }

    return [{ eventConsumer: 'meta', eventData: { ...fbCapiObject } }];
}

async function generateFacebookPixelObject(eventData: EventData) {
    const cookie = new Cookie();
    const cartLine = eventData.cart.CartLines.find((cartLine) => cartLine.CartLineItem.EdpNo === eventData.edpNo);
    const fbPixelObject: FbPixelObject = {
        content_ids: cartLine ? [cartLine.CartLineItem.ItemNo] : [],
        content_type: 'product_group',
        currency: 'CAD',
        user_data: {},
        content_name: eventData.cart.Products.find((product) => product.ItemNo === cartLine?.CartLineItem.ItemNo).Name,
        value: cartLine.InCart.PriceUnit, //eventData.cart.OrderSummary.GrandTotalAmount.toString(), //.MerchandiseSubTotal ?
        contents: cartLine
            ? [
                  {
                      id: cartLine.CartLineItem.ItemNo,
                      item_price: cartLine.InCart.PriceUnit,
                      delivery_category: 'home_delivery',
                      quantity: eventData.quantity,
                  },
              ]
            : [],
    };
    //add test code if exits
    if (window.tscConfig?.CapiTestCode) {
        fbPixelObject.test_event_code = window.tscConfig.CapiTestCode;
    }
    // If user is signed in, add user data
    if (cookie.UserAccessToken?.Token?.length > 0 && cookie.UserAccessToken.Edp > 0) {
        if (cookie.UserEmail) {
            fbPixelObject.user_data.em = await digestMessage(cookie.UserEmail.toLowerCase());
        }
        if (cookie.UserName) {
            fbPixelObject.user_data.fn = await digestMessage(cookie.UserName.toLowerCase());
        }
        if (cookie.LastName) {
            fbPixelObject.user_data.ln = await digestMessage(cookie.LastName.toLowerCase());
        }
    }

    return fbPixelObject;
}
